import { Contact } from '../../../types/Contact';

// Pretty phone formatter for display
export const formatPhoneDisplay = (raw?: string) => {
    if (!raw) return undefined;
    const s = String(raw).trim();
    const digits = s.replace(/[^\d+]/g, '');
    // +1XXXXXXXXXX or 1XXXXXXXXXX or XXXXXXXXXX
    const m = digits.match(/^\+?1?(\d{10})$/);
    if (m) {
        const d = m[1];
        return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    const m10 = digits.match(/^(\d{10})$/);
    if (m10) {
        const d = m10[1];
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    if (digits.startsWith('+')) {
        const d = digits.slice(1).replace(/\D/g, '');
        const cc = d.slice(0, 2);
        const rest = d.slice(2);
        return `+${cc} ${rest.replace(/(.{3})/g, '$1 ').trim()}`;
    }
    const just = digits.replace(/\D/g, '');
    if (!just) return s;
    return just.replace(/(.{3})/g, '$1 ').trim();
};

// Helpers: personal-first, company fallback selection
const normalize = (s?: string) => (s || '').toLowerCase();
const labelMatches = (label: string | undefined, patterns: string[]) => {
    const l = normalize(label);
    return patterns.some(p => l.includes(p));
};

export const getSelectedContactInfo = (formData: Contact) => {
    // Gather candidates for phones
    const phoneCandidates: Array<{ value?: string; label?: string; source: 'personal' | 'company' | 'generic'; key: string }>
        = [];

    // Multi phone labeled
    const mp = [
        { v: formData.phone_1_value, l: formData.phone_1_label, key: 'phone_1' },
        { v: formData.phone_2_value, l: formData.phone_2_label, key: 'phone_2' },
        { v: formData.phone_3_value, l: formData.phone_3_label, key: 'phone_3' },
    ];
    for (const p of mp) {
        if (!p.v) continue;
        const isPersonal = labelMatches(p.l, ['personal', 'mobile', 'cell', 'home']);
        const isCompany = labelMatches(p.l, ['work', 'company', 'office', 'business']);
        phoneCandidates.push({ value: p.v, label: p.l, source: isPersonal ? 'personal' : isCompany ? 'company' : 'generic', key: p.key });
    }
    // Legacy fields
    if (formData.mobile) phoneCandidates.push({ value: formData.mobile, label: 'mobile', source: 'personal', key: 'mobile' });
    if (formData.phone) phoneCandidates.push({ value: formData.phone, label: 'phone', source: 'generic', key: 'phone' });
    if (formData.business_phone) phoneCandidates.push({ value: formData.business_phone, label: 'business', source: 'company', key: 'business_phone' });

    const pickPhone = () => {
        const personal = phoneCandidates.find(p => p.source === 'personal');
        if (personal) return personal;
        const generic = phoneCandidates.find(p => p.source === 'generic');
        if (generic) return generic;
        return phoneCandidates.find(p => p.source === 'company');
    };

    // Emails
    const emailCandidates: Array<{ value?: string; label?: string; source: 'personal' | 'company' | 'generic'; key: string }>
        = [];
    const me = [
        { v: formData.email_1_value, l: formData.email_1_label, key: 'email_1' },
        { v: formData.email_2_value, l: formData.email_2_label, key: 'email_2' },
        { v: formData.email_3_value, l: formData.email_3_label, key: 'email_3' },
    ];
    for (const e of me) {
        if (!e.v) continue;
        const isPersonal = labelMatches(e.l, ['personal', 'home']);
        const isCompany = labelMatches(e.l, ['work', 'company', 'office', 'business']);
        emailCandidates.push({ value: e.v, label: e.l, source: isPersonal ? 'personal' : isCompany ? 'company' : 'generic', key: e.key });
    }
    if (formData.email) emailCandidates.push({ value: formData.email, label: 'email', source: 'generic', key: 'email' });
    if (formData.business_email) emailCandidates.push({ value: formData.business_email, label: 'business', source: 'company', key: 'business_email' });

    const pickEmail = () => {
        const personal = emailCandidates.find(e => e.source === 'personal');
        if (personal) return personal;
        const generic = emailCandidates.find(e => e.source === 'generic');
        if (generic) return generic;
        return emailCandidates.find(e => e.source === 'company');
    };

    // Websites
    const websiteCandidates: Array<{ value?: string; label?: string; source: 'personal' | 'company' | 'generic'; key: string }>
        = [];
    const mw = [
        { v: formData.website_1_value, l: formData.website_1_label, key: 'website_1' },
        { v: formData.website_2_value, l: formData.website_2_label, key: 'website_2' },
        { v: formData.website_3_value, l: formData.website_3_label, key: 'website_3' },
    ];
    for (const w of mw) {
        if (!w.v) continue;
        const isPersonal = labelMatches(w.l, ['personal', 'portfolio', 'blog']);
        const isCompany = labelMatches(w.l, ['work', 'company', 'business']);
        websiteCandidates.push({ value: w.v, label: w.l, source: isPersonal ? 'personal' : isCompany ? 'company' : 'generic', key: w.key });
    }
    if (formData.website_url) websiteCandidates.push({ value: formData.website_url, label: 'website', source: 'generic', key: 'website_url' });
    if (formData.website) websiteCandidates.push({ value: formData.website, label: 'website', source: 'generic', key: 'website' });
    if (formData.business_website) websiteCandidates.push({ value: formData.business_website, label: 'business', source: 'company', key: 'business_website' });

    const pickWebsite = () => {
        const personal = websiteCandidates.find(w => w.source === 'personal');
        if (personal) return personal;
        const generic = websiteCandidates.find(w => w.source === 'generic');
        if (generic) return generic;
        return websiteCandidates.find(w => w.source === 'company');
    };

    return {
        phone: pickPhone(),
        email: pickEmail(),
        website: pickWebsite(),
        linkedin: formData.linkedin_url,
    };
};

export const getWebsiteDisplay = (websiteValue?: string) => {
    if (!websiteValue) return undefined;
    try {
        let s = websiteValue.trim();
        s = s.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
        return s.split('/')[0];
    } catch {
        return websiteValue;
    }
};

// Generic copy helper for inline fields
export const copyField = async (label: string, value?: string | null, showNotification?: (message: string, type: 'success' | 'error') => void) => {
    const v = (value ?? '').toString().trim();
    if (!v || v === '—') return;
    try {
        await navigator.clipboard.writeText(v);
        showNotification?.(`${label} copied`, 'success');
    } catch {
        showNotification?.(`Failed to copy ${label.toLowerCase()}`, 'error');
    }
};